import React, { useState, useEffect } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
  styled,
  Button
} from '@mui/material';
import {
  Dashboard,
  Settings,
  Notifications,
  ExitToApp,
  Lock,
} from '@mui/icons-material';
import { useNavigate, Outlet } from 'react-router-dom';
import axios from 'axios';
import { companyInfo } from '../BaseURL';
import Swal from 'sweetalert2';

import ChangePasswordDialog from '../ChangePasswordDialog';

// ── Call ──────────────────────────────────────────────────────────────────────
import { CallProvider } from '../context/CallContext';
import IncomingCallModal from '../Call/IncomingCallModal';
import ActiveCallPanel from '../Call/ActiveCallPanel';

// ── Chat ──────────────────────────────────────────────────────────────────────
import { ChatProvider } from '../context/Chatcontext';
//  The file is named FloatingCallWidget but its only export is the CHAT widget.
//  It used to be imported twice under two names and rendered twice, stacked.
import ChatWidget from '../Call/FloatingCallWidget';

// ── Socket logout helper ──────────────────────────────────────────────────────
import { logoutUser } from '../services/socket';

const Main = styled('main')(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  marginLeft: 0,
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
}));

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  borderBottom: '1px solid',
  borderColor: theme.palette.divider,
  zIndex: theme.zIndex.drawer + 1,
}));

const ToolbarSpacer = styled('div')(({ theme }) => ({
  ...theme.mixins.toolbar,
}));

function MainLayout() {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [companyInfor, setcompanyInfor] = useState();
  const [logo, setlogo] = useState('');

  // Load user info
  useEffect(() => {
    const savedUserInfo = localStorage.getItem('userInfo');
    if (savedUserInfo) {
      setUserInfo(JSON.parse(savedUserInfo));
    } else {
      navigate('/login');
    }
  }, []);

  const loadCompang = async () => {
    try {
      const response = await axios.get(companyInfo);
      if (response.data.success) {
        setcompanyInfor(response.data.results);
      }
    } catch (error) {
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to Load Data',
        icon: 'error',
      });
    }
  };

  useEffect(() => { loadCompang(); }, []);

  useEffect(() => {
    if (companyInfor) setlogo(companyInfor[0].logo);
  }, [companyInfor]);

  const handleMenuClose = () => { setAnchorEl(null); setNotificationAnchorEl(null); };

  const handleLogout = () => {
    handleMenuClose();
    if (userInfo?.trans_code) {
      logoutUser(String(userInfo.trans_code));
    }
    localStorage.removeItem('userInfo');
    navigate('/');
  };

  const UserMenu = () => (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleMenuClose}
      PaperProps={{ sx: { width: 220, mt: 1.5 } }}
    >
      <MenuItem onClick={() => { handleMenuClose(); setShowChangePassword(true); }}>
        <ListItemIcon><Lock fontSize="small" /></ListItemIcon>
        <ListItemText>Change Password</ListItemText>
      </MenuItem>
      <Divider />
      <MenuItem onClick={handleLogout}>
        <ListItemIcon><ExitToApp fontSize="small" /></ListItemIcon>
        <ListItemText>Logout</ListItemText>
      </MenuItem>
    </Menu>
  );

  return (
    // CallProvider and ChatProvider both need the same user object
    <CallProvider user={userInfo}>
      <ChatProvider user={userInfo}>
        <Box sx={{ display: 'flex' }}>

          {/* ── App Bar ── */}
          <StyledAppBar position="fixed">
            <Toolbar>
              <Tooltip title="Go to Dashboard" arrow>
                <Box
                  onClick={() => navigate('/dashboard')}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    cursor: 'pointer',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1.5,
                    transition: 'background-color .15s ease',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  {logo && <img src={logo} alt="Logo" style={{ height: 38 }} />}
                </Box>
              </Tooltip>

              <Tooltip title="Dashboard" arrow>
                <Button
                  color="inherit"
                  startIcon={<Dashboard />}
                  onClick={() => navigate('/dashboard')}
                  sx={{
                    ml: 1,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    px: 1.75,
                    color: 'primary.main',
                    bgcolor: 'rgba(26,155,127,0.08)',
                    '&:hover': { bgcolor: 'rgba(26,155,127,0.16)' },
                  }}
                >
                  Dashboard
                </Button>
              </Tooltip>

              <Box sx={{ flexGrow: 1 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {userInfo?.role === 'Head Of Assembly' ||
                userInfo?.role === 'System Administrator' ? (
                  <Button
                    color="inherit"
                    startIcon={<Settings />}
                    onClick={() => navigate('/dashboard/AdminPanel')}
                    sx={{
                      bgcolor: 'action.hover',
                      '&:hover': { bgcolor: 'action.selected' },
                      textTransform: 'none',
                      px: 2,
                    }}
                  >
                    Admin Panel
                  </Button>
                ) : ''}

                {/* Staff chat — in the bar so it never floats over page content */}
                <ChatWidget />

                <IconButton
                  color="inherit"
                  sx={{ bgcolor: 'action.hover', '&:hover': { bgcolor: 'action.selected' } }}
                >
                  <Badge badgeContent={4} color="error">
                    <Notifications />
                  </Badge>
                </IconButton>

                <Avatar
                  sx={{
                    cursor: 'pointer',
                    bgcolor: 'primary.main',
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                >
                  {userInfo?.name?.charAt(0)}
                </Avatar>
              </Box>
            </Toolbar>
          </StyledAppBar>

          {/* ── Page content ── */}
          <Main className="main_side">
            <ToolbarSpacer />
            <Outlet />
          </Main>

          {/* ── Menus ── */}
          <UserMenu />

          <Menu
            anchorEl={notificationAnchorEl}
            open={Boolean(notificationAnchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: { mt: 1.5, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', minWidth: 300 },
            }}
          >
            <MenuItem onClick={handleMenuClose}>
              <Typography variant="inherit" noWrap>New staff member added</Typography>
            </MenuItem>
            <MenuItem onClick={handleMenuClose}>
              <Typography variant="inherit" noWrap>Report generated successfully</Typography>
            </MenuItem>
            <MenuItem onClick={handleMenuClose}>
              <Typography variant="inherit" noWrap>System update available</Typography>
            </MenuItem>
          </Menu>

          <ChangePasswordDialog
            open={showChangePassword}
            userInfo={userInfo}
            onClose={() => setShowChangePassword(false)}
          />

          {/* ── Call overlays (chat lives in the app bar above) ── */}
          <IncomingCallModal />
          <ActiveCallPanel />

        </Box>
      </ChatProvider>
    </CallProvider>
  );
}

export default MainLayout;